import ErreursConstante from "../../../_constantes/erreursConstantes.json";
import { OptionType } from "../../../components/AutoComplete";
import { formatterEspaces } from "../../../helpers/formatters";
import {
  isAlphaNumericHyphenAndUnderscore,
  isEmail,
  isInteger,
  isTelephonePortable,
} from "../../../helpers/validators";
import {
  AdministrateurBienDetail,
  Bien,
  BienDetail,
  BienDetailTypeResidenceEnum,
  CandidatLocataire,
  Commune,
  DossierLocation,
  NombrePieces,
  Site,
  TypeBien,
  TypeVoie,
  Utilisateur,
} from "../../../services/Api/swagger";

/**
 * @export
 * @enum {string}
 */
export enum ChoixParcours {
  FORCER_STATUT = "FORCER_STATUT",
}

export interface IBienFormData {
  numeroBatiment?: string;
  typeVoie?: string;
  numero?: string;
  adresse?: string;
  codePostalVille?: any;
  numeroLot?: string;
  typeBien?: string;
  meuble?: boolean;
  superficie?: number;
  nombrePieces?: string;
  loyerMensuel?: number;
  chargesMensuelles?: number;
  garantieLi?: "Avec" | "Sans";
  referenceMandat?: string;
  nomProprietaire?: string;
  prenomProprietaire?: string;
  mailProprietaire?: string;
  telephoneFixeProprietaire?: string;
  telephonePortableProprietaire?: string;
  choixParcours?: "parcoursADB" | "parcoursCandidatLocataire" | "";
  candidatLocataire?: {
    nom: string;
    prenom: string;
    email: string;
    telephonePortable: string;
    message?: string;
  };
}

export interface IBienFormError {
  identifiant?: string;
  numeroBatiment?: string;
  typeVoie?: string;
  numero?: string;
  adresse?: string;
  codePostalVille?: string;
  numeroLot?: string;
  typeBien?: string;
  meuble?: string;
  superficie?: string;
  nombrePieces?: string;
  loyerMensuel?: string;
  chargesMensuelles?: string;
  garantieLi?: string;
  referenceMandat?: string;
  nomProprietaire?: string;
  prenomProprietaire?: string;
  mailProprietaire?: string;
  telephoneFixeProprietaire?: string;
  telephonePortableProprietaire?: string;
  choixParcours?: string;
  // CANDIDAT LOCATAIRE
  candidatLocataire?: {
    nom?: string;
    prenom?: string;
    email?: string;
    telephonePortable?: string;
    message?: string;
  };
}

export const copyBienFormToModel = (bienForm: IBienFormData): Bien => {
  const { codePostalVille, candidatLocataire, choixParcours, ...bienData } =
    bienForm;
  const bien = {
    ...bienData,
    codePostal:
      bienForm.codePostalVille && bienForm.codePostalVille.value.split("-")[0],
    ville:
      bienForm.codePostalVille && bienForm.codePostalVille.value.split("-")[1],
    garantieLi:
      bienForm.garantieLi && bienForm.garantieLi === "Avec" ? true : false,
  } as Bien;

  return bien;
};

export const buildFormBien = (
  bienDetail?: BienDetail | Bien,
  isModification?: boolean,
  isParcoursCandidatLocataire?: boolean,
  dossierDuplique?: string
): IBienFormData => {
  let bienForm = {
    garantieLi: "Avec",
    typeResidence: BienDetailTypeResidenceEnum["PRINCIPALE"],
    choixParcours: dossierDuplique ? "parcoursADB" : undefined,
  } as IBienFormData;
  let choixParcours;
  if (isModification) {
    if (isParcoursCandidatLocataire) {
      choixParcours = "parcoursCandidatLocataire";
    } else {
      choixParcours = "parcoursADB";
    }
  }
  if (bienDetail) {
    const bien = copyBienDetailToBien(bienDetail);
    bienForm = {
      ...bien,
      choixParcours,
      codePostalVille: bien.codePostal &&
        bien.ville && {
          value: bien.codePostal + "-" + bien.ville,
          label: bien.ville + "(" + bien.codePostal + ")",
        },
      garantieLi: bien && bien.garantieLi === false ? "Sans" : "Avec",
      typeResidence:
        bien.typeResidence || BienDetailTypeResidenceEnum["PRINCIPALE"],
    } as IBienFormData;
  }
  return bienForm;
};

export const copyBienDetailToBien = (bienDetail: BienDetail | Bien) => {
  const { dateCreation, dateModification, ...bien } = bienDetail as BienDetail;
  return bien;
};

export const copyBienToBienDetail = (bien: Bien) => {
  const bienDetail = { ...bien } as BienDetail;
  return bienDetail;
};

export const validateForm = (values: IBienFormData): IBienFormError => {
  const errors: IBienFormError = {} as IBienFormError;
  if (!values.referenceMandat) {
    errors.referenceMandat =
      ErreursConstante.formulaire.generale.champObligatoire;
  }
  if (!values.numeroLot) {
    errors.numeroLot = ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values.typeVoie) {
    errors.typeVoie = ErreursConstante.formulaire.generale.champObligatoire;
  }
  if (values.numero && !isAlphaNumericHyphenAndUnderscore(values.numero)) {
    errors.numero = ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }

  if (
    values.numeroBatiment &&
    !isAlphaNumericHyphenAndUnderscore(values.numeroBatiment)
  ) {
    errors.numeroBatiment =
      ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }
  if (!values.adresse) {
    errors.adresse = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (
    !isAlphaNumericHyphenAndUnderscore(values.adresse.replace(/\s/g, ""))
  ) {
    errors.adresse = ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }
  if (!values.codePostalVille) {
    errors.codePostalVille =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values.typeBien) {
    errors.typeBien = ErreursConstante.formulaire.generale.champObligatoire;
  }
  if (
    !values.superficie &&
    values.choixParcours === "parcoursCandidatLocataire"
  ) {
    errors.superficie = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (values.superficie && !isInteger(values.superficie)) {
    errors.superficie = ErreursConstante.formulaire.generale.champEntierErreur;
  }
  if (
    !values.nombrePieces &&
    values.choixParcours === "parcoursCandidatLocataire"
  ) {
    errors.nombrePieces = ErreursConstante.formulaire.generale.champObligatoire;
  }
  if (!values.loyerMensuel) {
    errors.loyerMensuel = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (values.loyerMensuel && !isInteger(values.loyerMensuel)) {
    errors.loyerMensuel =
      ErreursConstante.formulaire.generale.champEntierErreur;
  }

  if (!values.chargesMensuelles) {
    errors.chargesMensuelles =
      ErreursConstante.formulaire.generale.champObligatoire;
  } else if (values.chargesMensuelles && !isInteger(values.chargesMensuelles)) {
    errors.chargesMensuelles =
      ErreursConstante.formulaire.generale.champEntierErreur;
  }
  if (!values.referenceMandat) {
    errors.referenceMandat =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (
    values &&
    values &&
    values.choixParcours === "parcoursCandidatLocataire" &&
    !values.candidatLocataire?.nom
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      nom: ErreursConstante.formulaire.generale.champObligatoire,
    };
  }

  // @ts-ignore
  if (values && (!values.choixParcours || values.choixParcours === "")) {
    errors.choixParcours =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (
    values &&
    values.choixParcours === "parcoursCandidatLocataire" &&
    !values.candidatLocataire?.prenom
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      prenom: ErreursConstante.formulaire.generale.champObligatoire,
    };
  }

  if (
    values &&
    values.choixParcours === "parcoursCandidatLocataire" &&
    !values.candidatLocataire?.email
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      email: ErreursConstante.formulaire.generale.champObligatoire,
    };
  } else if (
    values &&
    values.candidatLocataire?.email &&
    !isEmail(values.candidatLocataire?.email)
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      email: ErreursConstante.formulaire.generale.emailIncorrect,
    };
  }

  if (
    values &&
    values.choixParcours === "parcoursCandidatLocataire" &&
    !values.candidatLocataire?.telephonePortable
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      telephonePortable: ErreursConstante.formulaire.generale.champObligatoire,
    };
  } else if (
    values &&
    values.candidatLocataire?.telephonePortable &&
    !isTelephonePortable(values.candidatLocataire?.telephonePortable)
  ) {
    errors.candidatLocataire = {
      ...errors.candidatLocataire,
      telephonePortable:
        ErreursConstante.formulaire.generale.formatTelephonePortableIncorrect,
    };
  }

  return errors;
};

export const construireTypeVoieOptions = (typesVoie: TypeVoie[]) => {
  return typesVoie
    ? typesVoie.map((s) => ({ key: s.code, text: s.libelle, value: s.code }))
    : [];
};

export const construireTypesBienOptions = (typesBien: TypeBien[]) => {
  return typesBien
    ? typesBien.map((s) => ({ key: s.code, text: s.libelle, value: s.code }))
    : [];
};

export const construireNombrePiecesOptions = (nombrePieces: NombrePieces[]) => {
  return nombrePieces
    ? nombrePieces.map((s) => ({ key: s.code, text: s.libelle, value: s.code }))
    : [];
};

export const construireCommunes = (
  commune: Commune[]
): OptionType[] | undefined =>
  commune &&
  commune.map((option: Commune) => ({
    value: option.codePostal + "-" + option.nom,
    label: option.nom + " (" + option.codePostal + ")",
  }));

export const buildDossierLocation = (
  oldDossierLocation?: DossierLocation,
  selectedSite?: Site,
  adbDetail?: AdministrateurBienDetail,
  adbId?: number,
  utilisateurConnecte?: Utilisateur,
  choixParcours?: "parcoursADB" | "parcoursCandidatLocataire" | "",
  candidatLocataire?: CandidatLocataire
): DossierLocation | undefined => {
  const candidatLocataireFormatter = choixParcours ===
    "parcoursCandidatLocataire" &&
    candidatLocataire && {
      ...candidatLocataire,
      telephonePortable: formatterEspaces(candidatLocataire.telephonePortable),
    };
  if (!oldDossierLocation) {
    return {
      // si l'adbId n'existe pas c'est qu'il n'y a pas eu de sélection d'adb et donc que l'adb est unique
      referenceAdb:
        (adbId && adbId) || (adbDetail && adbDetail.identifiant) || 0,
      numeroSite: (selectedSite && selectedSite.numero) || 0,
      createur: (utilisateurConnecte && utilisateurConnecte.identifiant) || "",
      candidatLocataire: candidatLocataireFormatter || undefined,
    };
  } else {
    return {
      ...oldDossierLocation,
      candidatLocataire: candidatLocataireFormatter || undefined,
    };
  }
};
